import { useState } from 'react';
import { useToast } from '@crisellevaldez/speculo-ui';
import { useOrganizationStore } from '../stores/useOrganizationStore';
import { storage } from '../lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { isProduction } from '../config/environment';
import { DatabaseUploadFormData } from '../schemas/databaseUpload.schema';
import { createDatabaseUpload } from '../api/remiApi';
import { useOrganizationUsers } from './useOrganizationUsers';

interface UseDatabaseUploadReturn {
  submitDatabaseUpload: (data: DatabaseUploadFormData, file: File) => Promise<void>;
  isSubmitting: boolean;
  isSubmitted: boolean;
  resetSubmission: () => void;
}

// Webhook URL for database upload notifications
const WEBHOOK_URL = 'https://hooks.zapier.com/hooks/catch/12395623/uf1sp7c/';

/**
 * Custom hook for handling database upload form submission
 * Uploads file to storage, saves to Firestore, and triggers webhook
 */
export const useDatabaseUpload = (): UseDatabaseUploadReturn => {
  const { showToast } = useToast();
  const { organization, currentUser, fetchOrganization } = useOrganizationStore();
  // Used to hydrate `assignedAgents` ids on the webhook's `assignedTo`
  // field with `{ name, phoneNumber }` per agent.
  const { users: orgUsers } = useOrganizationUsers();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  /**
   * Upload file to Firebase Storage
   * Path: database-uploads/{orgId}/{filename}
   * Staging: database-uploads/{orgId}/{filename}
   * Production: heyisa-media/database-uploads/{orgId}/{filename}
   */
  const uploadFile = async (file: File, orgId: string): Promise<{ url: string; storagePath: string }> => {
    const timestamp = Date.now();
    const cleanFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const filename = `${timestamp}_${cleanFileName}`;

    const fullPath = isProduction()
      ? `heyisa-media/database-uploads/${orgId}/${filename}`
      : `database-uploads/${orgId}/${filename}`;

    const storageRef = ref(storage, fullPath);

    await uploadBytes(storageRef, file, {
      contentType: file.type,
    });

    const url = await getDownloadURL(storageRef);
    return { url, storagePath: fullPath };
  };

  /**
   * Save upload request to Remi API for tracking
   */
  const saveToRemiApi = async (
    data: DatabaseUploadFormData,
    file: File,
    storagePath: string,
    fileUrl: string,
    orgInfo: { orgId: string; orgName: string; userId: string; userEmail: string }
  ) => {
    const result = await createDatabaseUpload(orgInfo.orgId, {
      fileName: file.name,
      fileStoragePath: storagePath,
      fileSizeBytes: file.size,
      mimeType: file.type,
      importSettings: {
        fileUrl,
        orgName: orgInfo.orgName,
        userId: orgInfo.userId,
        userEmail: orgInfo.userEmail,
        aiOptedIn: data.aiOptedIn,
        flowTypes: data.aiOptedIn === 'yes' ? data.flowTypes : [],
        tags: data.tags || [],
        assignedTo: data.assignedTo || null,
        useAssignment: data.useAssignment || false,
        assignmentAction: data.useAssignment ? data.assignmentAction : '',
        assignedAgents: data.assignmentAction === 'assign' ? data.assignedAgents || [] : [],
        additionalNotes: data.additionalNotes || '',
      },
    });
    return result?.id ?? result?.request_id ?? '';
  };

  /**
   * Trigger webhook with form data and file URL
   * This is fire-and-forget - webhook failure should not block the upload
   */
  const triggerWebhook = async (
    data: DatabaseUploadFormData,
    fileUrl: string,
    orgInfo: { orgId: string; orgName: string; userId: string; userEmail: string }
  ) => {
    // Hydrate each `assignedAgents` id into `{ name, phoneNumber }` on
    // the webhook's `assignedTo` field, looked up from the org user
    // list. Zapier consumers were having to round-trip back to look
    // the agent up by id — including name + phone alongside the id
    // saves that hop. Unknown ids degrade to `{ id }` so the array
    // shape stays predictable. This only affects the Zapier payload;
    // the API/backend write below still receives `data.assignedTo`
    // unchanged (currently null in this modal).
    const agentIds =
      data.assignmentAction === 'assign' ? data.assignedAgents || [] : [];
    const assignedToHydrated = agentIds.map((id) => {
      const u = orgUsers.find((user) => user.id === id);
      if (!u) return { id };
      return {
        id: u.id,
        name: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email,
        phoneNumber: u.phone_number,
      };
    });

    const webhookPayload = {
      orgId: orgInfo.orgId,
      orgName: orgInfo.orgName,
      userId: orgInfo.userId,
      userEmail: orgInfo.userEmail,
      aiOptedIn: data.aiOptedIn,
      flowTypes: data.aiOptedIn === 'yes' ? data.flowTypes : [],
      tags: data.tags || [],
      assignedTo: assignedToHydrated.length > 0 ? assignedToHydrated : data.assignedTo || null,
      useAssignment: data.useAssignment || false,
      assignmentAction: data.useAssignment ? data.assignmentAction : '',
      assignedAgents: agentIds,
      additionalNotes: data.additionalNotes || '',
      fileUrl: fileUrl,
      submittedAt: new Date().toISOString(),
    };

    try {
      await fetch(WEBHOOK_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookPayload),
      });
    } catch (err) {
      console.warn('Webhook notification failed:', err);
    }
  };

  const submitDatabaseUpload = async (data: DatabaseUploadFormData, file: File) => {
    if (!organization || !currentUser) {
      showToast({
        message: 'Organization or user not found',
        variant: 'error',
      });
      throw new Error('Organization or user not found');
    }

    setIsSubmitting(true);

    try {
      const orgInfo = {
        orgId: organization.id,
        orgName: organization.org_name,
        userId: currentUser.uid,
        userEmail: currentUser.email,
      };

      const { url: fileUrl, storagePath } = await uploadFile(file, organization.id);
      await saveToRemiApi(data, file, storagePath, fileUrl, orgInfo);

      triggerWebhook(data, fileUrl, orgInfo);
      await fetchOrganization(true);

      setIsSubmitted(true);
      showToast({
        message: 'Database upload request submitted successfully!',
        variant: 'success',
      });
    } catch (error) {
      console.error('Error submitting database upload request:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to submit request',
        variant: 'error',
      });
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetSubmission = () => {
    setIsSubmitted(false);
  };

  return {
    submitDatabaseUpload,
    isSubmitting,
    isSubmitted,
    resetSubmission,
  };
};
